import { 
  doc, 
  getDoc, 
  setDoc, 
  onSnapshot, 
  serverTimestamp 
} from "firebase/firestore";

/**
 * Valores por defecto para Estado de Ruta
 */
export const DEFAULT_ROUTE_STATUS = {
  enabled: true,
  count: 4,
  labels: ['Go', 'Salimos', 'Volvemos', 'Fin'],
  shareWhatsAppAccess: true,
  shareFleetAlerts: true,
  updatedAt: null,
  updatedBy: null,
};

/**
 * Normaliza los datos del documento Firestore
 */
function normalizeRouteStatus(docData) {
  if (!docData) return DEFAULT_ROUTE_STATUS;

  return {
    enabled: docData.enabled !== false,
    count: docData.count || 4,
    labels: Array.isArray(docData.labels) ? docData.labels : DEFAULT_ROUTE_STATUS.labels,
    shareWhatsAppAccess: docData.shareWhatsAppAccess !== false,
    shareFleetAlerts: docData.shareFleetAlerts !== false,
    updatedAt: docData.updatedAt || null,
    updatedBy: docData.updatedBy || null,
  };
}

/**
 * Inicializa la configuración de Estado de Ruta
 * Si no existe, crea el documento con valores por defecto
 */
export async function initializeRouteStatus(workspaceId, db, userId) {
  if (!workspaceId || !db) {
    console.error("[ROUTE_STATUS_SYNC] Invalid workspaceId or db");
    return DEFAULT_ROUTE_STATUS;
  }

  try {
    const statusRef = doc(db, "workspaces", workspaceId, "settings", "routeStatus");
    console.log("[ROUTE_STATUS_SYNC] Initializing with ref:", statusRef.path);
    const statusSnap = await getDoc(statusRef);

    if (statusSnap.exists()) {
      console.log("[ROUTE_STATUS_SYNC] ✅ Config loaded from Firestore");
      return normalizeRouteStatus(statusSnap.data());
    }

    // No existe, crear con valores por defecto
    console.log("[ROUTE_STATUS_SYNC] Config not found, creating with defaults...");
    const defaultData = {
      ...DEFAULT_ROUTE_STATUS,
      updatedAt: serverTimestamp(),
      updatedBy: userId || 'system',
    };

    await setDoc(statusRef, defaultData);
    console.log("[ROUTE_STATUS_SYNC] ✅ Default config created successfully");
    return normalizeRouteStatus(defaultData);
  } catch (error) {
    console.error("[ROUTE_STATUS_SYNC] ❌ Error initializing route status:", error);
    return DEFAULT_ROUTE_STATUS;
  }
}

/**
 * Suscribe a cambios en tiempo real del documento de configuración
 * Retorna una función para desuscribirse
 */
export function subscribeToRouteStatus(workspaceId, db, onStatusChange) {
  if (!workspaceId || !db || !onStatusChange) {
    console.error("[ROUTE_STATUS_SYNC] Invalid parameters for subscribe");
    return () => {};
  }

  try {
    const statusRef = doc(db, "workspaces", workspaceId, "settings", "routeStatus");
    console.log("[ROUTE_STATUS_SYNC] Subscribing to:", statusRef.path);
    
    const unsubscribe = onSnapshot(
      statusRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const normalized = normalizeRouteStatus(snapshot.data());
          console.log("[ROUTE_STATUS_SYNC] 📡 Config updated from Firestore:", normalized);
          onStatusChange(normalized);
        } else {
          console.log("[ROUTE_STATUS_SYNC] Config not found, using defaults");
          onStatusChange(DEFAULT_ROUTE_STATUS);
        }
      },
      (error) => {
        console.error("[ROUTE_STATUS_SYNC] ❌ Subscription error:", error);
      }
    );

    console.log("[ROUTE_STATUS_SYNC] ✅ Subscription active");
    return unsubscribe;
  } catch (error) {
    console.error("[ROUTE_STATUS_SYNC] ❌ Error subscribing to route status:", error);
    return () => {};
  }
}

/**
 * Actualiza la configuración de Estado de Ruta en Firestore
 * Solo actualiza los campos proporcionados (merge)
 */
export async function updateRouteStatus(workspaceId, db, updates, userId) {
  if (!workspaceId || !db || !updates) {
    console.error("[ROUTE_STATUS_SYNC] Invalid parameters for update");
    return false;
  }

  try {
    const statusRef = doc(db, "workspaces", workspaceId, "settings", "routeStatus");
    console.log("[ROUTE_STATUS_SYNC] Updating route status at:", statusRef.path);
    
    const dataToUpdate = {
      ...updates,
      updatedAt: serverTimestamp(),
      updatedBy: userId || 'unknown',
    };

    await setDoc(statusRef, dataToUpdate, { merge: true });
    console.log("[ROUTE_STATUS_SYNC] ✅ Config updated in Firestore:", dataToUpdate);
    return true;
  } catch (error) {
    console.error("[ROUTE_STATUS_SYNC] ❌ Error updating route status:", error);
    console.error("[ROUTE_STATUS_SYNC] Error details:", error.message);
    return false;
  }
}
